import { useEffect, useState } from 'react';
import { GripVertical, Pencil, Trash2, X } from 'lucide-react';
import {
  useMission,
  type CameraAction,
  type VehicleAction,
  type Waypoint,
} from '@/store/mission';
import { useSelection } from '@/store/selection';
import { cn } from '@/lib/utils';

const CAMERA_ACTIONS: CameraAction[] = [
  'NONE',
  'TAKE_PHOTO',
  'START_PHOTO_INTERVAL',
  'STOP_PHOTO_INTERVAL',
  'START_VIDEO',
  'STOP_VIDEO',
  'START_PHOTO_DISTANCE',
  'STOP_PHOTO_DISTANCE',
];

const VEHICLE_ACTIONS: VehicleAction[] = [
  'NONE',
  'TAKEOFF',
  'LAND',
  'TRANSITION_TO_FW',
  'TRANSITION_TO_MC',
];

/**
 * Right-pane waypoint table — Bramer columns + inline editing.
 *
 * Columns: # | LAT | LON | ALT | SPD | HOLD | YAW | CAM | drag · edit · del
 *
 * Editable cells:
 *  - ALT, SPD, HOLD, YAW: click → input
 *  - CAM: dropdown
 *
 * Per-row affordances:
 *  - drag-handle (vertical-bars icon) — grab to reorder
 *  - edit (pencil) — opens the full detail panel (gimbal, fly-through, etc.)
 *  - delete (trash)
 */
export function WaypointPanel({
  open = false,
  onClose,
}: {
  open?: boolean;
  onClose?: () => void;
} = {}) {
  const waypoints = useMission((s) => s.waypoints);
  const selectedSeq = useMission((s) => s.selectedSeq);
  const setSelectedSeq = useMission((s) => s.setSelectedSeq);
  const removeWaypoint = useMission((s) => s.removeWaypoint);
  const updateWaypoint = useMission((s) => s.updateWaypoint);
  const reorderWaypoint = useMission((s) => s.reorderWaypoint);
  const [editorSeq, setEditorSeq] = useState<number | null>(null);

  useEffect(() => {
    if (!open || !onClose) return;
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') onClose!();
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  const selected = waypoints.find((w) => w.seq === selectedSeq) ?? null;
  const editor = waypoints.find((w) => w.seq === editorSeq) ?? null;

  return (
    <>
      <div
        onClick={onClose}
        aria-hidden
        className={cn(
          'fixed inset-0 z-40 bg-black/50 backdrop-blur-[2px] transition-opacity lg:hidden',
          open ? 'opacity-100' : 'pointer-events-none opacity-0',
        )}
      />
      <aside
        className={cn(
          'flex h-full flex-none flex-col border-l border-border-default bg-surface-1',
          'fixed inset-y-0 right-0 z-40 transition-transform duration-200 ease-out',
          'top-[var(--rail-topbar)]',
          open ? 'visible translate-x-0' : 'invisible translate-x-full',
          'lg:static lg:visible lg:translate-x-0 lg:top-0',
        )}
        style={{ width: 'var(--rail-right)' }}
      >
      {/* Header */}
      <header className="flex items-start justify-between border-b border-border-default px-4 py-3">
        <div className="min-w-0">
          <h2 className="m-0 font-mono text-[15px] tracking-[0.02em] text-fg-primary">Waypoints</h2>
          <p className="mt-0.5 font-mono text-[10px] uppercase tracking-[0.08em] text-fg-muted">
            {waypoints.length} pt(s) · drag # to reorder · click cell to edit · pencil for advanced
          </p>
        </div>
        {onClose && (
          <button
            onClick={onClose}
            title="Close waypoint panel"
            aria-label="Close"
            className="grid h-6 w-6 flex-none place-items-center rounded text-fg-secondary hover:bg-surface-2 hover:text-fg-primary lg:hidden"
          >
            <X className="h-3.5 w-3.5" />
          </button>
        )}
      </header>

      {/* Table */}
      <div className="flex-1 overflow-auto">
        {waypoints.length === 0 ? (
          <EmptyState />
        ) : (
          <table className="w-full font-mono text-[11px] tabular-nums">
            <thead className="sticky top-0 z-10 bg-surface-1">
              <tr className="border-b border-border-default text-[9px] uppercase tracking-[0.14em] text-fg-muted">
                <Th className="w-7">#</Th>
                <Th className="w-[58px]">LAT</Th>
                <Th className="w-[58px]">LON</Th>
                <Th className="w-10">ALT</Th>
                <Th className="w-10">SPD</Th>
                <Th className="w-10">HOLD</Th>
                <Th className="w-10">YAW</Th>
                <Th className="w-12">CAM</Th>
                <Th className="w-14" />
              </tr>
            </thead>
            <tbody>
              {waypoints.map((wp) => (
                <Row
                  key={wp.seq}
                  wp={wp}
                  selected={wp.seq === selectedSeq}
                  onSelect={() => {
                    setSelectedSeq(wp.seq);
                    useSelection.getState().setFocus('waypoint');
                  }}
                  onUpdate={(patch) => updateWaypoint(wp.seq, patch)}
                  onEdit={() => setEditorSeq(wp.seq)}
                  onDelete={() => removeWaypoint(wp.seq)}
                  onReorder={(toSeq) => reorderWaypoint(wp.seq, toSeq)}
                />
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Inline detail bar (when a row is selected but not editing) */}
      {selected && editorSeq === null && (
        <div className="border-t border-border-default p-3">
          <SectionLabel>Selected · #{selected.seq + 1}</SectionLabel>
          <div className="mt-2 grid grid-cols-2 gap-x-4 gap-y-1.5 font-mono text-[10.5px]">
            <KV label="LAT" value={selected.lat.toFixed(6)} />
            <KV label="LON" value={selected.lon.toFixed(6)} />
            <KV label="ALT" value={`${selected.alt_m}m`} />
            <KV
              label="FLY-THRU"
              value={selected.is_fly_through ? 'yes' : 'no'}
            />
            <KV
              label="GIMBAL"
              value={
                selected.gimbal_pitch_deg != null || selected.gimbal_yaw_deg != null
                  ? `${selected.gimbal_pitch_deg ?? 0}°/${selected.gimbal_yaw_deg ?? 0}°`
                  : '—'
              }
            />
            <KV label="VEHICLE" value={selected.vehicle_action ?? 'NONE'} />
          </div>
        </div>
      )}

      {/* Detail editor modal — covers panel with full per-waypoint form */}
      {editor && (
        <WaypointEditor
          wp={editor}
          onChange={(patch) => updateWaypoint(editor.seq, patch)}
          onClose={() => setEditorSeq(null)}
        />
      )}
      </aside>
    </>
  );
}

function Th({ children, className }: { children?: React.ReactNode; className?: string }) {
  return <th className={cn('px-1 py-1.5 text-left font-medium', className)}>{children}</th>;
}

function Row({
  wp,
  selected,
  onSelect,
  onUpdate,
  onEdit,
  onDelete,
  onReorder,
}: {
  wp: Waypoint;
  selected: boolean;
  onSelect: () => void;
  onUpdate: (patch: Partial<Waypoint>) => void;
  onEdit: () => void;
  onDelete: () => void;
  onReorder: (toSeq: number) => void;
}) {
  return (
    <tr
      draggable
      onDragStart={(e) => {
        e.dataTransfer.setData('text/x-fabricos-wp', String(wp.seq));
        e.dataTransfer.effectAllowed = 'move';
      }}
      onDragOver={(e) => {
        e.preventDefault();
        e.dataTransfer.dropEffect = 'move';
      }}
      onDrop={(e) => {
        e.preventDefault();
        const fromSeq = Number(e.dataTransfer.getData('text/x-fabricos-wp'));
        if (!Number.isNaN(fromSeq) && fromSeq !== wp.seq) onReorder(wp.seq);
      }}
      onClick={onSelect}
      className={cn(
        'group cursor-pointer border-b border-border-subtle',
        selected ? 'bg-accent-soft' : 'hover:bg-surface-2',
      )}
    >
      {/* Seq + drag handle */}
      <td className="px-1 py-1">
        <span className="flex items-center gap-1">
          <GripVertical className="h-3 w-3 cursor-grab text-fg-muted opacity-40 group-hover:opacity-100" />
          <span
            className="grid h-4 w-4 place-items-center rounded-full font-mono text-[9px] font-bold"
            style={{
              background: selected ? 'var(--color-status-good)' : 'var(--color-amber)',
              color: '#0e0f11',
            }}
          >
            {wp.seq + 1}
          </span>
        </span>
      </td>
      <td className="px-1 py-1 text-fg-secondary">{wp.lat.toFixed(4)}</td>
      <td className="px-1 py-1 text-fg-secondary">{wp.lon.toFixed(4)}</td>
      <EditableCell
        value={wp.alt_m}
        suffix="m"
        onChange={(v) => v != null && onUpdate({ alt_m: v })}
      />
      <EditableCell
        value={wp.speed_m_s ?? null}
        suffix="m/s"
        placeholder="—"
        onChange={(v) => onUpdate({ speed_m_s: v ?? undefined })}
      />
      <EditableCell
        value={wp.hold_s ?? null}
        suffix="s"
        placeholder="0"
        onChange={(v) => onUpdate({ hold_s: v ?? undefined })}
      />
      <EditableCell
        value={wp.yaw_deg ?? null}
        suffix="°"
        placeholder="—"
        onChange={(v) => onUpdate({ yaw_deg: v ?? undefined })}
      />
      <td className="px-1 py-1">
        <select
          value={wp.camera_action ?? 'NONE'}
          onClick={(e) => e.stopPropagation()}
          onChange={(e) =>
            onUpdate({
              camera_action: e.target.value === 'NONE'
                ? undefined
                : (e.target.value as CameraAction),
            })
          }
          className="h-5 w-full rounded-sm border border-border-subtle bg-base px-0.5 text-[10px] text-fg-primary outline-none focus:border-accent"
        >
          {CAMERA_ACTIONS.map((a) => (
            <option key={a} value={a}>
              {camLabel(a)}
            </option>
          ))}
        </select>
      </td>
      <td className="px-1 py-1">
        <div className="flex items-center gap-0.5">
          <button
            type="button"
            title="Edit advanced fields"
            onClick={(e) => {
              e.stopPropagation();
              onEdit();
            }}
            className="grid h-5 w-5 place-items-center rounded text-fg-muted hover:bg-surface-3 hover:text-accent"
          >
            <Pencil className="h-3 w-3" />
          </button>
          <button
            type="button"
            title="Delete waypoint"
            onClick={(e) => {
              e.stopPropagation();
              onDelete();
            }}
            className="grid h-5 w-5 place-items-center rounded text-fg-muted hover:bg-surface-3 hover:text-status-critical"
          >
            <Trash2 className="h-3 w-3" />
          </button>
        </div>
      </td>
    </tr>
  );
}

/** Compact inline-editable cell. Click → input; blur or Enter to commit. */
function EditableCell({
  value,
  suffix,
  placeholder,
  onChange,
}: {
  value: number | null;
  suffix?: string;
  placeholder?: string;
  onChange: (v: number | null) => void;
}) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(() => (value != null ? String(value) : ''));

  function start(e: React.MouseEvent) {
    e.stopPropagation();
    setDraft(value != null ? String(value) : '');
    setEditing(true);
  }
  function commit() {
    setEditing(false);
    const trimmed = draft.trim();
    if (trimmed === '') {
      onChange(null);
    } else {
      const v = Number(trimmed);
      if (Number.isFinite(v)) onChange(v);
    }
  }

  if (editing) {
    return (
      <td className="px-1 py-1" onClick={(e) => e.stopPropagation()}>
        <input
          autoFocus
          type="number"
          step={0.5}
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onBlur={commit}
          onKeyDown={(e) => {
            if (e.key === 'Enter') commit();
            if (e.key === 'Escape') setEditing(false);
          }}
          className="h-5 w-full rounded-sm border border-accent bg-base px-1 text-right text-[10.5px] text-fg-primary outline-none"
        />
      </td>
    );
  }
  return (
    <td className="cursor-text px-1 py-1 text-fg-primary" onClick={start}>
      {value != null ? (
        <span>
          {value}
          {suffix && <span className="text-fg-muted">{suffix}</span>}
        </span>
      ) : (
        <span className="text-fg-muted">{placeholder ?? '—'}</span>
      )}
    </td>
  );
}

function camLabel(a: CameraAction): string {
  switch (a) {
    case 'NONE':
      return '—';
    case 'TAKE_PHOTO':
      return 'PHOTO';
    case 'START_PHOTO_INTERVAL':
      return '+INT';
    case 'STOP_PHOTO_INTERVAL':
      return '-INT';
    case 'START_VIDEO':
      return '+VID';
    case 'STOP_VIDEO':
      return '-VID';
    case 'START_PHOTO_DISTANCE':
      return '+DST';
    case 'STOP_PHOTO_DISTANCE':
      return '-DST';
  }
}

function SectionLabel({ children }: { children: React.ReactNode }) {
  return (
    <div className="flex items-center gap-2">
      <span
        className="font-mono text-[10px] font-semibold uppercase tracking-[0.16em]"
        style={{ color: 'var(--color-accent)' }}
      >
        {children}
      </span>
      <span className="h-px flex-1" style={{ background: 'var(--color-accent-edge)' }} />
    </div>
  );
}

function KV({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="flex items-baseline justify-between gap-2">
      <span className="text-[9px] uppercase tracking-[0.14em] text-fg-muted">{label}</span>
      <span className="text-fg-primary">{value}</span>
    </div>
  );
}

/** Slide-in detail editor for the full Bramer field set: position, gimbal,
 *  fly-through, vehicle action, camera trigger params. */
function WaypointEditor({
  wp,
  onChange,
  onClose,
}: {
  wp: Waypoint;
  onChange: (patch: Partial<Waypoint>) => void;
  onClose: () => void;
}) {
  return (
    <div className="absolute inset-0 z-20 flex flex-col bg-surface-1">
      <header className="flex items-center justify-between border-b border-border-default px-4 py-3">
        <div>
          <h3 className="m-0 font-mono text-[14px] text-fg-primary">
            Waypoint #{wp.seq + 1} — advanced
          </h3>
          <p className="mt-0.5 font-mono text-[10px] uppercase tracking-[0.08em] text-fg-muted">
            All fields persist with the mission · save/load preserves them
          </p>
        </div>
        <button
          type="button"
          onClick={onClose}
          className="grid h-7 w-7 place-items-center rounded text-fg-secondary hover:bg-surface-2 hover:text-fg-primary"
        >
          <X className="h-4 w-4" />
        </button>
      </header>
      <div className="flex-1 overflow-y-auto overflow-x-hidden p-4 space-y-5">
        <Group title="Position">
          <NumField
            label="LAT"
            value={wp.lat}
            step={0.000001}
            precision={6}
            onChange={(v) => v != null && onChange({ lat: v })}
          />
          <NumField
            label="LON"
            value={wp.lon}
            step={0.000001}
            precision={6}
            onChange={(v) => v != null && onChange({ lon: v })}
          />
          <NumField
            label="ALT REL"
            unit="m"
            value={wp.alt_m}
            onChange={(v) => v != null && onChange({ alt_m: v })}
          />
        </Group>

        <Group title="Leg & arrival">
          <NumField
            label="SPEED"
            unit="m/s"
            value={wp.speed_m_s ?? null}
            placeholder="auto"
            onChange={(v) => onChange({ speed_m_s: v ?? undefined })}
          />
          <NumField
            label="HOLD"
            unit="s"
            value={wp.hold_s ?? null}
            placeholder="0"
            onChange={(v) => onChange({ hold_s: v ?? undefined })}
          />
          <NumField
            label="ACPT RAD"
            unit="m"
            value={wp.acceptance_radius_m ?? null}
            placeholder="2"
            onChange={(v) => onChange({ acceptance_radius_m: v ?? undefined })}
          />
          <BoolField
            label="FLY-THROUGH"
            value={wp.is_fly_through ?? false}
            onChange={(v) => onChange({ is_fly_through: v || undefined })}
          />
          <NumField
            label="YAW"
            unit="°"
            value={wp.yaw_deg ?? null}
            placeholder="auto"
            onChange={(v) => onChange({ yaw_deg: v ?? undefined })}
          />
        </Group>

        <Group title="Gimbal">
          <NumField
            label="PITCH"
            unit="°"
            value={wp.gimbal_pitch_deg ?? null}
            placeholder="—"
            onChange={(v) => onChange({ gimbal_pitch_deg: v ?? undefined })}
          />
          <NumField
            label="YAW"
            unit="°"
            value={wp.gimbal_yaw_deg ?? null}
            placeholder="—"
            onChange={(v) => onChange({ gimbal_yaw_deg: v ?? undefined })}
          />
        </Group>

        <Group title="Camera trigger">
          <SelectField
            label="ACTION"
            value={wp.camera_action ?? 'NONE'}
            options={CAMERA_ACTIONS.map((a) => ({ value: a, label: camLabel(a) }))}
            onChange={(v) =>
              onChange({
                camera_action: v === 'NONE' ? undefined : (v as CameraAction),
              })
            }
          />
          <NumField
            label="INTERVAL"
            unit="s"
            value={wp.camera_photo_interval_s ?? null}
            placeholder="—"
            onChange={(v) => onChange({ camera_photo_interval_s: v ?? undefined })}
          />
          <NumField
            label="DIST"
            unit="m"
            value={wp.camera_photo_distance_m ?? null}
            placeholder="—"
            onChange={(v) => onChange({ camera_photo_distance_m: v ?? undefined })}
          />
        </Group>

        <Group title="Vehicle action">
          <SelectField
            label="ACTION"
            value={wp.vehicle_action ?? 'NONE'}
            options={VEHICLE_ACTIONS.map((a) => ({ value: a, label: a }))}
            onChange={(v) =>
              onChange({
                vehicle_action: v === 'NONE' ? undefined : (v as VehicleAction),
              })
            }
          />
        </Group>
      </div>
    </div>
  );
}

function Group({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section>
      <SectionLabel>{title}</SectionLabel>
      <div className="mt-2 space-y-1.5">{children}</div>
    </section>
  );
}

function NumField({
  label,
  value,
  unit,
  step,
  precision,
  placeholder,
  onChange,
}: {
  label: string;
  value: number | null;
  unit?: string;
  step?: number;
  precision?: number;
  placeholder?: string;
  onChange: (v: number | null) => void;
}) {
  const display =
    value != null
      ? precision != null
        ? value.toFixed(precision)
        : String(value)
      : '';
  return (
    <div className="flex items-baseline justify-between gap-3 font-mono text-[11px]">
      <span className="tracking-[0.04em] text-fg-muted">{label}</span>
      <span className="flex items-baseline gap-1">
        <input
          type="number"
          step={step ?? 0.5}
          value={display}
          placeholder={placeholder}
          onChange={(e) => {
            const t = e.target.value.trim();
            if (t === '') onChange(null);
            else {
              const v = Number(t);
              if (Number.isFinite(v)) onChange(v);
            }
          }}
          className="h-6 w-28 rounded-sm border border-border-default bg-base px-1.5 text-right text-fg-primary tabular-nums outline-none focus:border-accent"
        />
        {unit && <span className="text-fg-muted">{unit}</span>}
      </span>
    </div>
  );
}

function BoolField({
  label,
  value,
  onChange,
}: {
  label: string;
  value: boolean;
  onChange: (v: boolean) => void;
}) {
  return (
    <div className="flex items-baseline justify-between gap-3 font-mono text-[11px]">
      <span className="tracking-[0.04em] text-fg-muted">{label}</span>
      <button
        type="button"
        onClick={() => onChange(!value)}
        className={cn(
          'h-6 w-28 rounded-sm border text-center text-[11px] tabular-nums transition-colors',
          value
            ? 'border-accent bg-accent/15 text-accent'
            : 'border-border-default bg-base text-fg-secondary hover:text-fg-primary',
        )}
      >
        {value ? 'YES' : 'NO'}
      </button>
    </div>
  );
}

function SelectField({
  label,
  value,
  options,
  onChange,
}: {
  label: string;
  value: string;
  options: Array<{ value: string; label: string }>;
  onChange: (v: string) => void;
}) {
  return (
    <div className="flex items-baseline justify-between gap-3 font-mono text-[11px]">
      <span className="tracking-[0.04em] text-fg-muted">{label}</span>
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="h-6 w-28 rounded-sm border border-border-default bg-base px-1 text-right text-fg-primary outline-none focus:border-accent"
      >
        {options.map((o) => (
          <option key={o.value} value={o.value}>
            {o.label}
          </option>
        ))}
      </select>
    </div>
  );
}

function EmptyState() {
  return (
    <div className="flex h-full flex-col items-center justify-center gap-2 px-6 py-10 text-center">
      <p className="m-0 font-mono text-[11px] uppercase tracking-[0.1em] text-fg-secondary">
        No waypoints yet
      </p>
      <p className="m-0 text-[11px] text-fg-muted">
        Click anywhere on the map to drop your first one.
      </p>
    </div>
  );
}
